import React, { useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Upload, Download, X, CheckCircle2, AlertTriangle, Loader2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic Excel import modal for clienti and prodotti.
 * Props:
 *   - open: boolean
 *   - onClose: fn
 *   - onImported: fn(insertedCount) — called after successful apply
 *   - resource: 'clienti' | 'prodotti'
 */
export default function ExcelImportModal({ open, onClose, onImported, resource }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const inputRef = useRef(null);

  const labelMap = { clienti: "Clienti", prodotti: "Prodotti" };
  const label = labelMap[resource] || resource;

  const reset = () => {
    setFile(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const close = () => { reset(); onClose && onClose(); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/${resource}/import/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `template_${resource}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleFile = async (f) => {
    setFile(f);
    setPreview(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/${resource}/import/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const onApply = async () => {
    if (!file) return;
    setApplying(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/${resource}/import/apply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ins = data.inserted || 0;
      const sk = (data.skipped || []).length;
      if (sk > 0) {
        toast.warning(`Importati ${ins} ${label.toLowerCase()} (${sk} saltati)`);
      } else {
        toast.success(`Importati ${ins} ${label.toLowerCase()}`);
      }
      onImported && onImported(ins);
      close();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/40 overflow-y-auto" onClick={close}>
      <div className="card-shell w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()} data-testid={`import-${resource}-modal`}>
        <div className="p-5 border-b flex justify-between items-center" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#E8F3EB", color: "var(--primary)" }}>
              <Upload className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Importa {label} da Excel</h2>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>Carica un file <code>.xlsx</code> con la prima riga di intestazione.</div>
            </div>
          </div>
          <button onClick={close} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={downloadTemplate}
              className="btn-secondary text-xs"
              data-testid={`import-${resource}-template`}
            >
              <Download className="w-3.5 h-3.5" /> Scarica template
            </button>
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              {resource === "clienti"
                ? "Colonne: nome, cognome_ragione_sociale, telefono, email, indirizzo, citta, cap, provincia, cf_piva, note, commessa_id, stato_pratica"
                : "Colonne: nome, sku, categoria, sottocategoria, costo_unitario, quantita_virtuale, quantita_fisica, scorta_minima, descrizione, fornitore, attivo"}
            </span>
          </div>

          <div
            className="border-2 border-dashed rounded-lg p-6 text-center"
            style={{ borderColor: "var(--border)", background: "var(--surface-muted)" }}
            data-testid={`import-${resource}-dropzone`}
          >
            <FileSpreadsheet className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--primary)" }} />
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xlsm"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              className="hidden"
              data-testid={`import-${resource}-file-input`}
            />
            {!file && (
              <button
                onClick={() => inputRef.current?.click()}
                className="btn-primary text-sm"
                data-testid={`import-${resource}-pick`}
              >
                Scegli file Excel…
              </button>
            )}
            {file && (
              <div className="text-sm">
                <div className="font-semibold">{file.name}</div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{(file.size / 1024).toFixed(1)} KB</div>
                <button
                  onClick={() => { reset(); setTimeout(() => inputRef.current?.click(), 0); }}
                  className="btn-secondary text-xs mt-2"
                >
                  Cambia file
                </button>
              </div>
            )}
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Analisi del file in corso…
            </div>
          )}

          {preview && (
            <div data-testid={`import-${resource}-preview`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: "#E6F4EA", color: "#1B5E20" }}>
                  <CheckCircle2 className="inline w-3.5 h-3.5 mr-1" /> {preview.valid} validi
                </span>
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: "#FDECEA", color: "#B30000" }}>
                  <AlertTriangle className="inline w-3.5 h-3.5 mr-1" /> {preview.errors} con errori
                </span>
                <span className="text-xs" style={{ color: "var(--text-muted)" }}>su {preview.total} righe totali</span>
              </div>

              <div className="border rounded-lg overflow-hidden" style={{ borderColor: "var(--border)" }}>
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead style={{ background: "var(--surface-muted)" }}>
                      <tr>
                        <th className="px-2 py-2 text-left">Riga</th>
                        <th className="px-2 py-2 text-left">Stato</th>
                        <th className="px-2 py-2 text-left">{resource === "clienti" ? "Nome / Cognome" : "Nome / SKU"}</th>
                        <th className="px-2 py-2 text-left">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.items.map((it) => (
                        <tr key={it.row} className="border-t" style={{ borderColor: "var(--border)", background: it.valid ? "transparent" : "#FFF5F5" }}>
                          <td className="px-2 py-1.5 font-mono">{it.row}</td>
                          <td className="px-2 py-1.5">
                            {it.valid ? (
                              <span className="text-[11px]" style={{ color: "#1B5E20" }}>OK</span>
                            ) : (
                              <span className="text-[11px]" style={{ color: "#B30000" }}>Saltata</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            {resource === "clienti"
                              ? `${it.nome || "—"} ${it.cognome_ragione_sociale || ""}`.trim()
                              : `${it.nome || "—"} ${it.sku ? "(" + it.sku + ")" : ""}`}
                          </td>
                          <td className="px-2 py-1.5" style={{ color: it.valid ? "var(--text-muted)" : "#B30000" }}>
                            {it.errors.length > 0 ? it.errors.join(", ") : (resource === "clienti" ? `comm: ${it.commessa_id?.slice(0, 8) || ""}` : `${it.categoria || ""}`)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
            <button onClick={close} className="btn-secondary" data-testid={`import-${resource}-cancel`}>Annulla</button>
            <button
              onClick={onApply}
              disabled={!preview || preview.valid === 0 || applying}
              className="btn-primary disabled:opacity-50"
              data-testid={`import-${resource}-apply`}
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Importa {preview?.valid || 0} {label.toLowerCase()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
